/**
 * DraftsTabContent Component
 * Content for the Drafts tab - shows draft coaching sessions
 */

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { CoachingSessionCard } from "@/components/cards/CoachingSessionCard"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useDeleteCoachingSession,
  useMyCoachingSessions,
  usePublishCoachingSession,
} from "@/features/coaching/hooks/useCoachingMutations"
import { useDebounce } from "@/hooks/useDebounce"

interface DraftsTabContentProps {
  userId: string
  searchQuery?: string
  className?: string
}

export function DraftsTabContent({ userId, searchQuery, className }: DraftsTabContentProps) {
  const [localSearch] = useState("")
  const debouncedSearch = useDebounce(localSearch || searchQuery || "", 500)
  const navigate = useNavigate()
  const deleteSession = useDeleteCoachingSession()
  const publishSession = usePublishCoachingSession()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState<{ id: string; title: string } | null>(null)

  const {
    data: coachingData,
    isLoading,
    isError,
  } = useMyCoachingSessions(userId, {
    limit: 50,
    search: debouncedSearch || undefined,
    status: "draft",
  })

  const draftSessions = coachingData?.sessions ?? []

  const handleDeleteClick = (sessionId: string, sessionTitle: string) => {
    setSessionToDelete({ id: sessionId, title: sessionTitle })
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (sessionToDelete) {
      deleteSession.mutate(sessionToDelete.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false)
          setSessionToDelete(null)
        },
      })
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setSessionToDelete(null)
  }

  return (
    <div className={className}>
      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-stone">Loading...</p>
        </div>
      )}

      {isError && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-red">Error loading drafts</p>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="mt-0 px-5">
          {draftSessions.length === 0 ? (
            <div className="h-[250px] mt-4">
              <p className="text-stone mt-3 text-[14px] font-regular leading-[25px]">
                No drafts yet. Save a session as draft to see it here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 my-5 md:my-10 w-full">
              {draftSessions.map(session => (
                <CoachingSessionCard
                  key={session.id}
                  id={session.id}
                  slug={session.slug}
                  title={session.title}
                  coverImage={session.coverImage}
                  format={session.format}
                  duration={session.duration}
                  participationFee={session.participationFee}
                  price={session.price}
                  description={session.description}
                  tags={session.tags}
                  creator={session.creator}
                  isOwner={session.isOwner}
                  isDraft={true}
                  location={session.streetAddress}
                  stats={session.stats}
                  onEditClick={
                    session.isOwner
                      ? () => navigate(`/coaching-sessions/edit/${session.id}`)
                      : undefined
                  }
                  onDeleteClick={
                    session.isOwner ? () => handleDeleteClick(session.id, session.title) : undefined
                  }
                  onPublishClick={
                    session.isOwner ? () => publishSession.mutate(session.id) : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={open => {
          setDeleteDialogOpen(open)
          if (!open) {
            setSessionToDelete(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Draft</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{sessionToDelete?.title}"? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteSession.isPending}
            >
              {deleteSession.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
